// ./src/components/features/EventPartners/PartnerKind/PartnerKind.tsx
//
// How a PARTNER KIND looks, everywhere it appears.
//
// ⚠ ONE IMPLEMENTATION, three pages. Sponsors & Partners, Packages Offered and Applications are the
// same vocabulary seen from three angles, and a host reading them expects a sponsor to look like a
// sponsor on all of them. They had drifted: one page rendered a coloured icon and a tinted badge,
// the other two a plain grey label — so the same partner read as a different thing depending on
// which page you were on.
//
// ⚠ The icon and colour come from `partnerKindMeta`, which is the shared vocabulary — not from a
// table repeated per page. A kind added to PARTNER_KIND later picks up its look automatically.
import React from "react";
import { Badge, ThemeIcon } from "@mantine/core";

import { AppIcon } from "@/components/generic";
import { partnerKindLabel, partnerKindMeta } from "@/utils/api";

/** The kind as a tinted, icon-led badge — for a row's secondary line. */
export const PartnerKindBadge: React.FC<{ kind?: string | null; size?: "xs" | "sm" | "md" }> = React.memo(
  ({ kind, size = "sm" }) => {
    if (!kind) {
      return null;
    }

    const meta = partnerKindMeta(kind);

    return (
      <Badge size={size} variant="light" color={meta.color} leftSection={<AppIcon iconName={meta.icon} size={11} />}>
        {partnerKindLabel(kind)}
      </Badge>
    );
  },
);

PartnerKindBadge.displayName = "PartnerKindBadge";

/**
 * The kind as a round, coloured avatar — the leading element of a row.
 *
 * ⚠ Falls back to the `other` kind rather than rendering nothing: a row with no avatar collapses its
 * left column and the whole table jumps, which reads as a broken row rather than a missing value.
 */
export const PartnerKindIcon: React.FC<{ kind?: string | null; size?: number }> = React.memo(({ kind, size = 34 }) => {
  const meta = partnerKindMeta(kind);

  return (
    <ThemeIcon variant="light" color={meta.color} radius="xl" size={size}>
      <AppIcon iconName={meta.icon} size={Math.round(size * 0.55)} />
    </ThemeIcon>
  );
});

PartnerKindIcon.displayName = "PartnerKindIcon";
